import React from 'react';
import { motion } from 'framer-motion';
import { FiSearch, FiTrendingUp, FiCheckCircle, FiArrowRight, FiLink, FiFileText, FiSettings, FiBarChart, FiAward, FiShield } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const TraditionalSEOPage = () => {
  useDocumentTitle(
    'Trusted Indian SEO Company With a Proven SEO Expert in India',
    'Work with an Indian SEO company that combines simple strategies and clear communication. Led by a skilled SEO expert in India, we help you rank higher and grow steadily.'
  );

  const features = [
    {
      icon: FiSearch,
      title: "On-Page Optimization",
      description: "Enhance every page with keyword targeting, improved structure, meta data refinement, UX fixes, and search-intent alignment. Essential for top-tier visibility."
    },
    {
      icon: FiLink,
      title: "Link Building",
      description: "High-quality backlinks that strengthen domain authority, improve trust, and elevate long-term rankings."
    },
    {
      icon: FiSettings,
      title: "Technical SEO",
      description: "Fix speed, indexing, mobile issues, site architecture, schema, and crawl paths to ensure search engines can properly understand and rank your site."
    },
    {
      icon: FiFileText,
      title: "Content Strategy",
      description: "Create SEO-focused content built around search intent, user needs, and conversion goals. Strong content is the backbone of every successful campaign."
    }
  ];

  const whyChooseUs = [
    {
      icon: FiAward,
      title: "Proven Track Record",
      description: "With years of experience and a reputation as one of the best SEO expert in India, our strategies deliver consistent results."
    },
    {
      icon: FiBarChart,
      title: "Transparent Reporting",
      description: "Clear monthly dashboards, ranking updates, traffic growth, and performance analytics—100% transparent."
    },
    {
      icon: FiShield,
      title: "White-Hat Methods",
      description: "No shortcuts. No spam. Only ethical and sustainable SEO designed for long-term success."
    }
  ];

  const seoProcess = [
    {
      number: "01",
      title: "SEO Audit & Analysis",
      description: "Deep analysis of your current SEO status including errors, gaps, opportunities, competitors, and ranking potential."
    },
    {
      number: "02",
      title: "Keyword Research",
      description: "Data-driven keyword discovery matched with business intent, industry trends, and what customers are actually searching for."
    },
    {
      number: "03",
      title: "On-Page Optimization",
      description: "Optimize URLs, metadata, headings, content flow, internal linking, schema, and UX signals."
    },
    {
      number: "04",
      title: "Content Creation",
      description: "Build authoritative content around keywords and user intent to improve both rankings and engagement."
    },
    {
      number: "05",
      title: "Link Building",
      description: "Secure relevant, credible, and high-authority backlinks through ethical outreach, PR, and partnerships."
    },
    {
      number: "06",
      title: "Monitor & Optimize",
      description: "Real-time improvements using analytics, heatmaps, AI insights, and competitor tracking."
    }
  ];

  const pricingPlans = [
    {
      name: "Starter",
      price: "₹15,000",
      period: "/month",
      description: "Ideal for SEO services for small business",
      features: [
        "10 Target Keywords",
        "Monthly SEO Report",
        "Basic On-Page Fixes",
        "Link Building (Starter Level)",
        "Google Analytics Setup",
        "Email Support"
      ],
      highlighted: false
    },
    {
      name: "Professional",
      price: "₹30,000",
      period: "/month",
      description: "Perfect for growth-focused companies",
      features: [
        "20 Target Keywords",
        "Full On-Page SEO",
        "Technical Fixes",
        "Link Building (Standard)",
        "Keyword Map + Content Plan",
        "2 Blogs/Month",
        "Dedicated Support"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For large companies seeking the best SEO agency in India",
      features: [
        "Unlimited Keywords",
        "Advanced Technical SEO",
        "Competitor Intelligence",
        "In-Depth Authority Building",
        "Full Content Strategy",
        "Monthly Consultation",
        "Priority Support"
      ],
      highlighted: false
    }
  ];

  const faqs = [
    {
      question: "What is Traditional SEO?",
      answer: "Traditional SEO is the process of improving your website through on-page optimization, technical fixes, keyword targeting, and high-quality backlinks to achieve long-term organic rankings."
    },
    {
      question: "Why is Traditional SEO important for business growth?",
      answer: "Traditional SEO helps your business gain long-lasting visibility, attract consistent organic traffic, and build authority in competitive markets without relying only on paid ads."
    },
    {
      question: "How does Traditional SEO improve search rankings?",
      answer: "Traditional SEO improves rankings by optimizing content, fixing technical issues, strengthening internal linking, adding schema markup, and building relevant high-authority backlinks."
    },
    {
      question: "What is the difference between Traditional SEO and AI-SEO?",
      answer: "Traditional SEO focuses on keywords, on-page fixes, links, and technical improvements. AI-SEO focuses on entities, structured answers, content intelligence, and AEO formats for AI-driven search engines."
    },
    {
      question: "Why does SEO take time to show results?",
      answer: "SEO takes time because search engines need to crawl, index, evaluate relevance, compare competitors, and monitor user behavior before improving your position in search results."
    },
    {
      question: "How do you build high-quality backlinks?",
      answer: "Backlinks are built through authority outreach, niche blogs, PR mentions, industry directories, and content partnerships—never spam or automated links."
    },
    {
      question: "What happens if my rankings suddenly drop?",
      answer: "Ranking drops are analyzed through algorithm updates, competitor activity, technical issues, and content gaps. Once the cause is identified, corrective actions are applied to restore performance."
    },
    {
      question: "Why should a small business invest in SEO?",
      answer: "SEO helps small businesses appear in front of ready-to-buy customers, compete with larger brands, and generate high-intent leads without heavy advertising costs."
    },
    {
      question: "How often will you update and optimize my website?",
      answer: "Your website is updated continuously through monthly audits, keyword refinements, content upgrades, and technical adjustments based on analytics and ranking trends."
    },
    {
      question: "What type of content improves SEO performance?",
      answer: "Content that answers user intent—blogs, service pages, FAQs, guides, product pages, and AI-ready structured content—helps improve rankings and trust."
    },
    {
      question: "Why is technical SEO necessary?",
      answer: "Technical SEO ensures your site loads fast, is mobile-friendly, indexable, secure, and easy for search engines to understand—leading to higher performance and better rankings."
    },
    {
      question: "How do you measure SEO success?",
      answer: "SEO success is measured through keyword rankings, organic traffic, conversions, engagement metrics, link growth, and overall website visibility."
    },
    {
      question: "How long does it take to see SEO results?",
      answer: "Most websites begin seeing improvements within 60–90 days depending on competition, niche, and current SEO health."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden bg-gradient-to-br from-orange-600 via-red-600 to-orange-700">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-6xl font-bold text-white mb-6">
                Traditional SEO Services
              </h1>
              <p className="text-2xl text-white/90 mb-8 leading-relaxed">
                Proven strategies that stand the test of time. Grow your organic traffic with a perfect mix of on-page, off-page, technical SEO, and content optimization. As a best SEO expert in India, we help brands achieve stable, long-term rankings with clean white-hat methods.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
                >
                  <span>Start Ranking Higher</span>
                  <FiArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300"
                >
                  <span>Get Free Audit</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is SEO Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">What is SEO?</h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-6">
              SEO is the foundation of sustainable ranking growth. It includes keyword research, content improvements, technical fixes, UX enhancements, and authority-building with ethical link acquisition.
            </p>
            <p className="text-xl text-gray-600 leading-relaxed">
              Whether you're looking for SEO services for small business or want to scale an established brand, this method ensures steady, compounding visibility across search engines.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="text-3xl text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-5xl font-bold text-center text-gray-800 mb-16">Why Choose Our SEO?</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyChooseUs.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-orange-50 to-red-50 p-8 rounded-2xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                  <div className="bg-white w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-md">
                    <IconComponent className="text-3xl text-orange-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SEO Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Our SEO Process</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              A structured, AI-ready, AEO-friendly workflow engineered to reach and maintain top search rankings.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {seoProcess.map((step, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="text-5xl font-bold text-orange-600 mb-4">{step.number}</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-5xl font-bold text-center text-gray-800 mb-4">SEO Packages</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Choose the perfect plan for your business needs. All plans include expert management and optimization.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg p-8 transform hover:scale-105 transition-all duration-300 ${
                  plan.highlighted ? 'ring-4 ring-blue-500 shadow-2xl' : 'hover:shadow-2xl'
                }`}
              >
                {plan.highlighted && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-orange-600">{plan.price}</span>
                  {plan.period && <span className="text-gray-600">{plan.period}</span>}
                </div>
                <p className="text-gray-600 mb-6">{plan.description}</p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`block text-center px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                    plan.highlighted
                      ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white hover:shadow-lg'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-5xl font-bold text-center text-gray-800 mb-4">FAQ's</h2>
          <p className="text-center text-gray-600 mb-12">
            Common questions about Traditional SEO
          </p>
          
          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="font-semibold text-lg text-gray-800 cursor-pointer flex justify-between items-center">
                  {faq.question}
                  <FiCheckCircle className="text-orange-600 w-5 h-5 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 via-red-600 to-orange-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Dominate Search Rankings?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Partner with India's leading SEO experts and watch your organic traffic soar
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-2 bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
          >
            <span>Get Your Free SEO Audit</span>
            <FiArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TraditionalSEOPage;
